import { BaseDialog, IBaseDialogProps } from '@enter/core';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { ErrorDialog } from './error-dialog';
import { IErrorPayload, parseError } from './util';

export interface IDialogProps {
    props: Omit<IBaseDialogProps, 'open'>;
    Component: React.ReactNode;
}

export interface IDialogContextProps {
    showDialog: (props: IDialogProps) => void;
    hideDialog: () => void;
    showError: (err: Error) => void;
    hideError: () => void;
}

export const DialogContext = React.createContext<IDialogContextProps>({
    showDialog: () => undefined,
    hideDialog: () => undefined,
    showError: () => undefined,
    hideError: () => undefined,
});

const ignoredErrorNames = ['AbortError', 'TypeError'];
const ignoredErrorCodes = ['err.application_access_token_has_expired'];

/**
 * `DemoModeBlockedError` (core `demo-mode.utils.ts`), matched by name like the ignored errors above.
 *
 * In demo mode a request with no generated answer is refused before it leaves the browser. That
 * must stay VISIBLE — a quiet failure is indistinguishable from a demo that has stopped working
 * (NS-14) — but it is not a system exception. Sales hit this on Patient Collections (2026-09-10):
 * a prospect was shown a trace, an API URL and "report this error" for a request that was
 * deliberately never sent.
 */
const DEMO_BLOCKED_ERROR_NAME = 'DemoModeBlockedError';
const DEMO_BLOCKED_TITLE = 'Not available in demo mode';
const DEMO_BLOCKED_MESSAGE =
    'This part of Enter works with live practice data, so it is switched off while demo mode is on. Nothing was sent.';

export const DialogProvider: React.FC<{ children?: React.ReactNode }> = ({ children }): React.ReactElement => {
    const [dialogProps, setDialogProps] = useState<IDialogProps>();
    const [isOpen, setOpen] = useState(false);
    const [error, setError] = useState<Error>();
    const [payload, setPayload] = useState<IErrorPayload>();

    useEffect(() => {
        (async () => {
            if (error) {
                if (error.name === DEMO_BLOCKED_ERROR_NAME) {
                    setPayload({ message: DEMO_BLOCKED_MESSAGE, hideTrace: true, demoBlocked: true });
                } else if (!_.includes(ignoredErrorNames, error.name)) {
                    const handled = await parseError(error);
                    if (!_.includes(ignoredErrorCodes, handled.payload.code)) {
                        setPayload(handled.payload);
                    }
                } else {
                    // tslint:disable-next-line:no-console
                    console.warn('Ignored error:', error);
                }
            }
        })();
    }, [error]);

    const showDialog = (props: IDialogProps): void => {
        setDialogProps(props);
        setOpen(true);
    };

    const hideDialog = (): void => {
        setOpen(false);
        // Drop content so dialog children unmount; otherwise the same Component type is
        // reconciled on reopen and local state (e.g. Resolve Invoice notes) is kept.
        setDialogProps(undefined);
    };

    const showError = (err: Error): void => {
        setError(err);
    };

    const hideError = (): void => {
        setError(undefined);
        setPayload(undefined);
    };

    return (
        <DialogContext.Provider value={{ showDialog, hideDialog, showError, hideError }}>
            {children}
            {dialogProps && (
                <BaseDialog {...dialogProps.props} open={isOpen}>
                    {dialogProps && dialogProps.Component}
                </BaseDialog>
            )}
            {payload && (
                <BaseDialog
                    disableBackdropClick={false}
                    disableEscapeKeyDown={false}
                    renderTitle={payload.demoBlocked ? DEMO_BLOCKED_TITLE : 'A system exception has occurred'}
                    onClose={hideError}
                    open={true}>
                    <ErrorDialog payload={payload} onClose={hideError} />
                </BaseDialog>
            )}
        </DialogContext.Provider>
    );
};
